import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Collapsible from 'react-collapsible';
import queryString from 'query-string';
import { gql, useQuery } from '@apollo/client';
import CommentsCTAButtons from './CommentsCTAButtons';
import MultiBoardsCollapsibleHeaderTitle from './MultiBoardsCollapsibleHeaderTitle';
import { CommentsView } from '../index';
import './styles.scss';

const GET_BOARDS_FOR_LIVECAMS = gql`
    query BoardsForLivecams($ids: [String!]!) {
        boardsForLivecams(
            input: {livecam_legacy_ids: $ids}
        ) {
            board_id
            livecam_canonical_uri
        }
    }
`;

const defaultProps = {
  partner: null,
  commentToken: null,
};

const propTypes = {
  partner: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    has_email: PropTypes.bool,
  }),
  livecams: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    canonical_url: PropTypes.string,
  })).isRequired,
};

const MultiBoards = ({ partner, livecams }) => {
  const [currentLivecamId, setCurrentLivecamId] = useState();

  const { data, loading } = useQuery(GET_BOARDS_FOR_LIVECAMS, {
    variables: { ids: livecams.map(livecam => String(livecam.id)) },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (currentLivecamId) {
      window.history.pushState({}, '', `?livecam=${currentLivecamId}`);
    }
  }, [currentLivecamId]);

  const currentLivecam = useMemo(() => {
    return livecams.find(({ id }) => id?.toString() === currentLivecamId?.toString());
  }, [currentLivecamId, livecams]);

  const buttons = useMemo(() => {
    const uniqueBoards = new Map();

    livecams.forEach((item, key) => {
      const boardId = data?.boardsForLivecams[key]?.board_id;
      if (!boardId) return;
      if (!uniqueBoards.has(boardId)) {
        uniqueBoards.set(boardId, {
          livecamId: null,
          livecamIndexes: [],
        });
      }
      uniqueBoards.get(boardId).livecamId = item.id;
      uniqueBoards.get(boardId).livecamIndexes.push(key + 1);
    });

    // eslint-disable-next-line no-unused-vars
    return Array.from(uniqueBoards).map(([_, { livecamId, livecamIndexes }]) => ({
      livecamId,
      livecamIndexes,
      isActive: livecamId?.toString() === currentLivecamId?.toString(),
    }));
  }, [currentLivecamId, livecams, data]);

  useEffect(() => {
    if (buttons.length > 0) {
      const query = queryString.parse(window.location.search);
      const { livecam } = query;
      setCurrentLivecamId(livecam || buttons[0].livecamId);
    }

    return () => {
      setCurrentLivecamId(null);
    };
  }, [data]);

  if (!currentLivecamId || !currentLivecam || loading) {
    return null;
  }

  return (
    <Collapsible
      trigger={(
        <MultiBoardsCollapsibleHeaderTitle
          buttons={buttons}
          onLivecamChange={setCurrentLivecamId}
        />
      )}
      open
      triggerClassName="Collapsible__trigger container-rounded text-uppercase u-font-foundation-roman u-textSize-20 pY-15"
      triggerOpenedClassName="Collapsible__trigger is-open container-top-rounded text-uppercase u-font-foundation-roman u-textSize-20 pY-15"
      contentOuterClassName="info-box-content u-font-foundation-roman u-textSize-18"
      contentInnerClassName="disqus-height"
    >
      <CommentsCTAButtons partner={partner} livecamId={currentLivecamId} />
      <CommentsView livecamId={currentLivecamId} pageUrl={currentLivecam.canonical_url} compactView />
    </Collapsible>
  );
};

const mapStateToProps = (state) => {
  return {
    partner: state.currentItem.data.partner,
    livecams: state.currentItem.data &&
      state.currentItem.nonPrimaryFeeds &&
      !state.currentItem.data.isFetching ? [
        state.currentItem.data,
        ...state.currentItem.nonPrimaryFeeds,
      ] : [],
  };
};

MultiBoards.propTypes = propTypes;
MultiBoards.defaultProps = defaultProps;

export default connect(mapStateToProps)(MultiBoards);
